import { ApplicationRef, Injectable } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { Observable, of, Subject } from 'rxjs';
import { ApiService } from 'core/api/api.service';

export interface AdvancedSearchRequestVars {
  recordList$: Subject<any>;
  recordCount$: Subject<any>;
  resultCount: {
    exact: number;
    partial: number;
  };
}

export interface RequestObject {
  url: string;
}

export interface AdvancedSearchRequest {
  exact: Observable<any>;
  partial: Observable<any>;
}

@Injectable({
  providedIn: 'root'
})

export class AdvancedSearchService {

  constructor(
    private apiService: ApiService,
    private app: ApplicationRef
  ) { }

  getOffsetsForPage(payload, requestVars: AdvancedSearchRequestVars, pageSize: number) {
    const page = payload.page;
    let exactOffset = null;
    let partialOffset = null;
    if (page === 1) {
      requestVars.resultCount.exact = 0;
      requestVars.resultCount.partial = 0;
      exactOffset = 0;
      partialOffset = 0;
    } else {
      const resultOffset = (page - 1) * pageSize;
      // if we're still showing exact matches
      if (resultOffset < requestVars.resultCount.exact) {
        exactOffset = resultOffset;
        // and we need to show partial results
        if ((page * pageSize) > requestVars.resultCount.exact) {
          partialOffset = 0;
        }
      } else {
        // we're only displaying partial results
        partialOffset = resultOffset - requestVars.resultCount.exact;
      }
    }

    // Check if we have any text-searchable fields that warrant partial search
    const hasNameFilters =
      payload.nameFilter?.name?.trim().length ||
      payload.nameFilter?.bpNumber?.trim().length ||
      payload.nameFilter?.customerName?.trim().length ||
      payload.nameFilter?.orderNumber?.toString().trim().length ||
      payload.nameFilter?.quoteNumber?.toString().trim().length ||
      payload.nameFilter?.invoiceNumber?.toString().trim().length ||
      payload.nameFilter?.totalAmount?.toString().trim().length ||
      payload.nameFilter?.poNumber?.trim().length ||
      payload.nameFilter?.quoteName?.trim().length ||
      payload.nameFilter?.dollarAmount?.toString().trim().length ||
      payload.nameFilter?.partNumber?.trim().length ||
      payload.nameFilter?.contact?.trim().length;

    const hasAdvancedFilters =
      payload.advancedFilter?.priceMatrix?.trim().length ||
      payload.advancedFilter?.city?.trim().length ||
      payload.advancedFilter?.postalCode?.trim().length ||
      payload.advancedFilter?.stateId ||
      payload.advancedFilter?.countryId;

    if (!hasNameFilters && !hasAdvancedFilters) {
      partialOffset = null;
    }

    return {
      exactOffset,
      partialOffset
    };
  }

  getResultsForOffset<T>(requestObject: any, partialSearchCondition: boolean, offset: number) {
    const requestObjCopy = JSON.parse(JSON.stringify(requestObject));

    requestObjCopy.payload.offset = offset;
    delete requestObjCopy.payload.page;

    requestObjCopy.payload.nameFilter.partialSearch = partialSearchCondition;

    return this.apiService.post<T>(requestObjCopy.url, requestObjCopy.payload);
  }

  getRequestObservables<T>(type: (new () => T), requestObj, offsets): AdvancedSearchRequest {
    const obj = {
      exact: of(new type()),
      partial: of(new type())
    };

    if (offsets.exactOffset !== null) {
      obj.exact = this.getResultsForOffset(requestObj, false, offsets.exactOffset);
    }
    if (offsets.partialOffset !== null) {
      obj.partial = this.getResultsForOffset(requestObj, true, offsets.partialOffset);
    }

    return obj;
  }

  resetSort(tableSort: MatSort, sort?: MatSort) {
    if (tableSort) {
      if (sort) {
        tableSort.active = sort.active;
        tableSort.direction = sort.direction;
        tableSort.sortChange.emit({ active: sort.active, direction: sort.direction });
        tableSort._stateChanges.next();
      } else {
        tableSort.sort({ id: '', start: 'asc', disableClear: true });
      }
    }
  }

}
